import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SidebarContext = createContext();

// Mobile breakpoint (matches --bp-md in design-system.css)
const MOBILE_BREAKPOINT = 768;

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};

export const SidebarProvider = ({ children }) => {
    // Load initial state from localStorage
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('sidebarCollapsed');
        return saved === 'true';
    });

    // Mobile state
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
    );
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < MOBILE_BREAKPOINT;
            setIsMobile(mobile);
            // Close mobile drawer when switching to desktop
            if (!mobile) {
                setIsMobileOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Save to localStorage when state changes
    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', isCollapsed);
        // Update CSS custom property for responsive components (only on desktop)
        if (!isMobile) {
            document.documentElement.style.setProperty(
                '--sidebar-width',
                isCollapsed ? '72px' : '280px'
            );
        }
    }, [isCollapsed, isMobile]);

    // Set sidebar width to 0 on mobile
    useEffect(() => {
        if (isMobile) {
            document.documentElement.style.setProperty('--sidebar-width', '0px');
        } else {
            document.documentElement.style.setProperty(
                '--sidebar-width',
                isCollapsed ? '72px' : '280px'
            );
        }
    }, [isMobile, isCollapsed]);

    const toggleSidebar = () => {
        setIsCollapsed(prev => !prev);
    };

    const toggleMobileMenu = useCallback(() => {
        setIsMobileOpen(prev => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMobileOpen(false);
    }, []);

    return (
        <SidebarContext.Provider value={{
            isCollapsed,
            toggleSidebar,
            isMobile,
            isMobileOpen,
            toggleMobileMenu,
            closeMobileMenu
        }}>
            {children}
        </SidebarContext.Provider>
    );
};

export default SidebarContext;
